import {
  Stack,
  Box,
  Grid,
  Button,
  LinkBox,
  LinkOverlay,
  Popover,
  PopoverAnchor,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  Portal,
  Text,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC, Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { LabeledLectureCard } from '@/components/event/LabeledLectureCard';
import { formatTimezoneDuration } from '@/components/event/utils';
import { SessionTooltip } from '@/components/ui/features/session/components/SessionTooltip';
import { AddToCalendarSessionButton } from '@/components/ui/shared/buttons/AddToCalendar/AddToCalendarSessionButton';
import { LectureSearchCardSkeletons } from '@/components/ui/shared/lecture/LectureSearchCard/components/LectureSearchCardSkeletons';
import { ShowMore } from '@/components/ui/shared/text/ShowMoreText/ShowMore';
import { SearchCategories } from '@/enums/index';
import { useAddToCalendarDescription } from '@/hooks/useAddToCalendarDescription';
import { useLectureSearch } from '@/hooks/useLectureSearch';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useUserEventTimezone } from '@/hooks/useUserEventTimezone';
import { ThinEventSession } from '@/resources/ThinEventSession';
import { StringParam } from '@/utils/url-query-string';

const ShowMoreInitial = dynamic(
  () => import('@/components/ui/shared/text/ShowMoreText/ShowMoreInitial').then((mod) => mod.ShowMoreInitial),
  { ssr: false }
);

interface IStackProps {
  children: ReactNode;
  eventSessionId?: string;
  externalUrl?: string;
  eventId?: string;
}

const InitialTextStack: FC<IStackProps> = ({ children, eventSessionId, externalUrl, eventId }) => {
  const router = useRouter();
  const underlineUrl = `${process.env.NEXT_PUBLIC_BASE_URL}events/${eventId}/schedule`;
  const baseUrl = `${externalUrl || underlineUrl}?`;

  const newParams = {
    ...router.query,
    eventSessionId,
  } as Record<string, string>;

  const queryParamsString = new URLSearchParams(newParams).toString();

  return (
    <Stack
      m="2"
      justify="space-between"
      align={{ base: 'space-between', sm: 'flex-start' }}
      spacing="2"
      background={'white'}
      pt="4"
      pr="8"
      pb="6"
      pl="8"
      borderRadius="2"
      _hover={{
        boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.08)',
        border: '1px solid #E3E5E5',
      }}
    >
      <LinkOverlay as={NextLink} target="_blank" href={baseUrl + queryParamsString} />
      {children}
    </Stack>
  );
};

const CommonStack: FC<IStackProps> = ({ children }) => {
  return (
    <Stack
      m="2"
      justify="space-between"
      align={{ base: 'space-between', sm: 'flex-start' }}
      spacing="2"
      padding="2"
      borderRadius="2"
      style={{ cursor: 'default' }}
    >
      {children}
    </Stack>
  );
};

interface IEventSessionProps {
  eventSession: ThinEventSession;
  scope: SearchCategories;
}

const PAGE_SIZE = 8;

export const EventSessionEmbed: FC<IEventSessionProps> = ({ eventSession, scope }) => {
  const { eventId, eventSessionId, search } = useQueryParams<{
    eventId: string;
    eventSessionId: string;
    search: string;
  }>({
    eventId: StringParam,
    search: StringParam,
  });
  const addToCalendarDescription = useAddToCalendarDescription(eventSession.id);

  const { t } = useTranslation();

  const userEventTimezone = useUserEventTimezone(eventId);

  const isPoster = scope === SearchCategories.POSTERS;

  const { data, error, size, setSize, isReachingEnd, isLoadingMore, isEmpty } = useLectureSearch(
    {
      event_id: eventId,
      event_session_id: eventSessionId || eventSession.id,
      poster_lecture: isPoster.toString(),
      published: 'all',
    },
    {
      initialSize: eventSessionId ? 2 : 1,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const startsAtFormatted = formatTimezoneDuration(eventSession.startsAt, userEventTimezone, 'MMMM dd HH:mm');
  const endsAtFormatted = formatTimezoneDuration(eventSession.endsAt, userEventTimezone, 'MMMM dd HH:mm');

  const EvaluatedTextStack = eventSessionId ? CommonStack : InitialTextStack;

  return (
    <Fragment key={`fragment-${eventSession.id}`}>
      <LinkBox cursor={'pointer'}>
        <Popover
          arrowPadding={15}
          strategy="fixed"
          placement="top"
          openDelay={500}
          isLazy
          flip
          preventOverflow
          lazyBehavior="unmount"
          trigger="hover"
        >
          <PopoverAnchor>
            <Box position="sticky" top={0} justifySelf="center" />
          </PopoverAnchor>
          <PopoverTrigger>
            <EvaluatedTextStack eventSessionId={eventSession.id} externalUrl={eventSession.url} eventId={eventId}>
              <Box
                fontFamily="body"
                fontSize="16px"
                display={{ md: 'flex', base: 'block' }}
                justifyContent="space-between"
                w="100%"
              >
                <Box display="flex" alignItems="center">
                  {`${startsAtFormatted} - ${endsAtFormatted}`}
                </Box>
                <Box mt={{ base: '4', md: '0' }}>
                  <AddToCalendarSessionButton size={20} simpleMenuButton session={eventSession} color="black" />
                  {eventSession.ctaLabel && eventSession.ctaUrl && (
                    <Button as={NextLink} href={eventSession.ctaUrl} mr="4" size="sm">
                      {eventSession.ctaLabel}
                    </Button>
                  )}
                </Box>
              </Box>
              <Box fontWeight="bold" fontSize="30px" mt="4">
                {eventSession.name}
              </Box>
              <Box fontSize="16px" fontWeight="600" mt="8px" textTransform="uppercase">
                {eventSession.chair && t('eventResource.sessionChair')}{' '}
                {eventSession.chair && <Text as="span" fontWeight="normal">{`${eventSession.chair}`}</Text>}
                {eventSession.chair && eventSession.venue && ` | `}
                {eventSession.venue && t('eventResource.location')}
                <Text as="span" fontWeight="normal">
                  {eventSession.venue}
                </Text>
              </Box>
              {eventSession?.description && (
                <Box fontFamily="body" mb="6">
                  {eventSessionId ? (
                    <ShowMore text={eventSession?.description} />
                  ) : (
                    <ShowMoreInitial text={eventSession?.description} />
                  )}
                </Box>
              )}
            </EvaluatedTextStack>
          </PopoverTrigger>
          <Portal>
            <PopoverContent w="100%">
              <PopoverArrow />
              <SessionTooltip title={eventSession.name} session={eventSession} />
            </PopoverContent>
          </Portal>
        </Popover>
      </LinkBox>
      {!isEmpty && eventSessionId && !search && (
        <Grid
          templateColumns="repeat(auto-fill, min(100%, 290px))"
          gap="40px"
          pb="4"
          key={`lectures-container-${eventSession.id}`}
          pl="4"
        >
          {data.map((lecture) => (
            <LabeledLectureCard key={lecture.id} lecture={lecture} eventId={eventId} />
          ))}
          <LectureSearchCardSkeletons size={PAGE_SIZE} isLoading={isLoadingMore && !isReachingEnd} />
        </Grid>
      )}
      <InView
        as="span"
        rootMargin="20px 0px 0px 0px"
        onChange={(inView) => {
          if (inView && !error && !isReachingEnd && eventSessionId) {
            setSize(size + 1);
          }
        }}
      >
        <span />
      </InView>
    </Fragment>
  );
};
